import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from '../../utils/axios';
import { styled, alpha } from '@mui/material/styles';
import { MenuProps } from '@mui/material/Menu';
import {
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Skeleton,
  Box,
  Typography,
  Tooltip,
  Drawer,
  Container,
  MenuItem,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';

import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Payment as PaymentIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RestartAlt as ResetZoomIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchExpensePayments,
  deleteExpensePayment,
  exportExpenseReport,
  fetchExpenseCategories,
  fetchExpenseVendors,
  approveExpensePayment,
  rejectExpensePayment
} from '../../store/expenseStore/expenseThunk';
import { ApiStatus, apiLimit, defaultRowsPerPageOptions } from '../../utils/api.types';
import { ExpenseFilters, ExpensePayment } from '../../utils/expense.types';
import { Utils } from '../../utils/common-utils';
import CustomDataGrid from '../DataGrid/DataGrid';
import ExpenseFilter from './ExpenseFilter';
import CustomSelect from '../FormFields/CustomSelect';
import DialogAddExpenseForm from './DialogAddExpenseForm';
import DialogSettleExpense from './DialogSettleExpense';
import DialogDocAttachForm from '../DocumentAttachment/DialogDocAttachForm';
import AuditTimeline from '../AuditTimeline/AuditTimeline';

import Menu from '@mui/material/Menu';
import LockIcon from '@mui/icons-material/Lock';
import useModuleAccess from '../../hooks/useModuleAccess';

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow: 'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': { padding: '4px 0' },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': { fontSize: 18, color: theme.palette.text.secondary, marginRight: theme.spacing(1.5) },
      '&:active': { backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity) },
    },
  },
}));

interface ExpenseListProps {
  accountType?: string;
}

const ExpenseList: FC<ExpenseListProps> = (props) => {
  const dispatch = useAppDispatch();
  const { payments, rowCount, categories, categoryDropdownItems, vendors, paymentsStatus, status, errorMessage } = useAppSelector(state => state.expense);
  const { selectedAccount, user, modulePermissions } = useAppSelector(state => state.auth);

  // Module grants for this page. Approve/reject is driven by the expenses:approve
  // grant rather than isAdmin, so a non-admin who was given "Can Approve Expenses"
  // actually sees the buttons — isAdmin would have hidden them.
  const { can, isAccountOwner, canAccessModule } = useModuleAccess();

  // Check if logged-in user is admin/manager/owner. isAccountOwner also covers the
  // Admin-Level Access overlay, which leaves the user's role as Member — the role
  // check alone hid the admin-only actions from those users.
  const isAdmin = isAccountOwner || selectedAccount?.owner?._id === user?._id || selectedAccount?.owner === user?._id || user?.role?.some((r: any) => ['admin', 'Admin', 'ADMIN', 'manager', 'Manager', 'MANAGER', 'Owner', 'owner', 'OWNER'].includes(r.role));
  const canApprove = can('expenses', 'approve');
  const canCreateExpense = can('expenses', 'create');
  const canExportExpense = can('expenses', 'export');

  // Check if logged-in user is an employee (non-admin)
  const isEmployeeUser = !isAdmin && user?.isEmployee && user?.employeeId;
  const loggedInEmployeeId = user?.employeeId;

  // null until the date filter reports its range, so no unfiltered fetch fires first
  const [filters, setFilters] = useState<ExpenseFilters | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [openExpenseDrawer, setOpenExpenseDrawer] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<ExpensePayment | null>(null);
  const [isRefresh, setIsRefresh] = useState('');
  const [openSettleDialog, setOpenSettleDialog] = useState(false);
  const [expenseToSettle, setExpenseToSettle] = useState<ExpensePayment | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionMenuExpense, setActionMenuExpense] = useState<ExpensePayment | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<{ open: boolean; attachments: any[]; currentIndex: number }>({ open: false, attachments: [], currentIndex: 0 });
  const [imgZoom, setImgZoom] = useState(100);
  const [imgPan, setImgPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [editExpense, setEditExpense] = useState<ExpensePayment | null>(null);
  // Expense opened from global search: shown first on the first page, whatever the filters.
  const [searchParams, setSearchParams] = useSearchParams();
  const pinId = searchParams.get('pin');
  const [pinnedExpense, setPinnedExpense] = useState<ExpensePayment | null>(null);
  useEffect(() => {
    if (!pinId) { setPinnedExpense(null); return; }
    axios.get(`/api/v1/invoice/expense-payments`, { params: { expenseId: pinId, page: 0, limit: 1 } })
      .then((res: any) => setPinnedExpense(res.data?.payments?.[0] || null))
      .catch(() => setPinnedExpense(null));
  }, [pinId, isRefresh]);

  // Confirmation dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approveReason, setApproveReason] = useState('');
  const [dialogExpense, setDialogExpense] = useState<ExpensePayment | null>(null);

  // Audit log dialog
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [auditExpense, setAuditExpense] = useState<ExpensePayment | null>(null);

  const handleOpenAudit = (expense: ExpensePayment) => {
    setAuditExpense(expense);
    setAuditDialogOpen(true);
  };

  const handleCloseAudit = () => {
    setAuditDialogOpen(false);
    setAuditExpense(null);
  };

  // Memoized callback for filter changes - reset to first page
  const handleFiltersChange = useCallback((newFilters: ExpenseFilters) => {
    setPage(0);
    setFilters(newFilters);
  }, []);

  // Load initial data on component mount
  useEffect(() => {
    if (selectedAccount) {
      dispatch(fetchExpenseCategories({ flat: false }));
      if (modulePermissions && canAccessModule('suppliers')) dispatch(fetchExpenseVendors({ accountId: selectedAccount._id }));
    }
  }, [dispatch, selectedAccount, modulePermissions]);

  // Layout effect so the fetch starts before paint — avoids a frame of "No Expenses Yet"
  useLayoutEffect(() => {
    if (selectedAccount && filters) {
      const finalFilters = { ...filters, page, limit: pageSize };
      // For employee users, always filter by their employee ID
      if (isEmployeeUser && loggedInEmployeeId) {
        finalFilters.employee = loggedInEmployeeId;
      }
      const request = dispatch(fetchExpensePayments(finalFilters));
      return () => request.abort();
    }
  }, [dispatch, selectedAccount, filters, page, pageSize, isRefresh, isEmployeeUser, loggedInEmployeeId]);

  useEffect(() => {
    if (page > 0 && rowCount > 0 && page * pageSize >= rowCount) {
      setPage(Math.max(0, Math.ceil(rowCount / pageSize) - 1));
    }
  }, [page, pageSize, rowCount]);

  const handleRefresh = () => {
    setIsRefresh(Date.now().toString());
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const visiblePayments = useMemo(() => {
    const allPayments = payments || [];
    const isClientSidePaginationNeeded =
      allPayments.length > pageSize || (rowCount > 0 && allPayments.length === rowCount);

    if (!isClientSidePaginationNeeded) {
      return allPayments;
    }

    const startIndex = page * pageSize;
    return allPayments.slice(startIndex, startIndex + pageSize);
  }, [page, pageSize, payments, rowCount]);

  const shownPayments = useMemo(() => (pinnedExpense && page === 0
    ? [pinnedExpense, ...visiblePayments.filter((p: any) => p._id !== pinnedExpense._id)]
    : visiblePayments), [pinnedExpense, page, visiblePayments]);

  const handleOpenExpense = () => { setEditExpense(null); setOpenExpenseDrawer(true); };

  const handleEditExpense = (expense: ExpensePayment) => {
    handleCloseActionMenu();
    setEditExpense(expense);
    setOpenExpenseDrawer(true);
  };

  const handleCloseExpense = async (val: any) => {
    if (val) {
      handleRefresh();
    }
    setOpenExpenseDrawer(false);
    setEditExpense(null);
  };

  const handleDeleteExpense = async (expenseId: string) => {
    try {
      await dispatch(deleteExpensePayment(expenseId)).unwrap();
      handleRefresh();
    } catch (error) {
      console.error('Failed to delete expense:', error);
    }
  };

  const handleExport = async (format: 'excel' | 'pdf' = 'excel') => {
    try {
      await dispatch(exportExpenseReport({ format, filters: filters || {} })).unwrap();
    } catch (error) {
      console.error('Failed to export report:', error);
    }
  };

  const handleOpenActionMenu = (event: React.MouseEvent<HTMLElement>, expense: ExpensePayment) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuExpense(expense);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuExpense(null);
  };

  const handleSettleExpense = (expense: ExpensePayment) => {
    handleCloseActionMenu();
    setExpenseToSettle(expense);
    setOpenSettleDialog(true);
  };

  const handleCloseSettleDialog = (settled?: boolean) => {
    setOpenSettleDialog(false);
    setExpenseToSettle(null);
    if (settled) {
      handleRefresh();
    }
  };

  // Open confirmation dialogs
  const handleDeleteFromMenu = (expense: ExpensePayment) => {
    handleCloseActionMenu();
    setDialogExpense(expense);
    setDeleteDialogOpen(true);
  };

  const handleApproveExpense = (expense: ExpensePayment) => {
    handleCloseActionMenu();
    setDialogExpense(expense);
    setApproveDialogOpen(true);
  };

  const handleRejectExpense = (expense: ExpensePayment) => {
    handleCloseActionMenu();
    setDialogExpense(expense);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  // Confirm actions
  const confirmDelete = async () => {
    if (dialogExpense) {
      await handleDeleteExpense(dialogExpense._id);
    }
    setDeleteDialogOpen(false);
    setDialogExpense(null);
  };

  const confirmApprove = async () => {
    if (dialogExpense) {
      try {
        await dispatch(approveExpensePayment({ expenseId: dialogExpense._id, remarks: approveReason || undefined })).unwrap();
        handleRefresh();
      } catch (error) {
        console.error('Failed to approve expense:', error);
      }
    }
    setApproveDialogOpen(false);
    setDialogExpense(null);
    setApproveReason('');
  };

  const confirmReject = async () => {
    if (dialogExpense) {
      try {
        await dispatch(rejectExpensePayment({ expenseId: dialogExpense._id, reason: rejectReason || undefined })).unwrap();
        handleRefresh();
      } catch (error) {
        console.error('Failed to reject expense:', error);
      }
    }
    setRejectDialogOpen(false);
    setDialogExpense(null);
    setRejectReason('');
  };

  const getPaymentModeColor = (mode: string) => {
    const colors: { [key: string]: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' } = {
      'CASH': 'success',
      'CHEQUE': 'primary',
      'CARD': 'secondary',
      'UPI': 'info',
      'NETBANKING': 'warning',
    };
    return colors[mode] || 'default';
  };

  const getStatusChipStyle = (status: string) => {
    const styles: { [key: string]: { bg: string; color: string; label: string } } = {
      'BOOKED': { bg: '#fff3e0', color: '#e65100', label: 'Booked' },
      'APPROVED': { bg: '#e3f2fd', color: '#1565c0', label: 'Approved' },
      'PAID': { bg: '#e8f5e9', color: '#2e7d32', label: 'Paid' },
      'REJECTED': { bg: '#ffebee', color: '#c62828', label: 'Rejected' },
      'EXPENSE_RECORDED': { bg: '#f3e5f5', color: '#7b1fa2', label: 'Recorded' },
    };
    return styles[status] || { bg: '#f5f5f5', color: '#616161', label: status };
  };

  const getCategoryName = (categoryCode: string) => {
    const category = categories?.find(cat => cat.code === categoryCode);
    return category?.name || categoryCode;
  };

  /**
   * Column definitions for expense payments data grid.
   */
  // Shared tooltip look for the "who / when" line above each remark block.
  const noteTooltipProps = {
    tooltip: { sx: { backgroundColor: '#1f2937', color: '#f9fafb', fontSize: '0.75rem', fontWeight: 500, px: 1.5, py: 0.75, borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.25)' } },
    arrow: { sx: { color: '#1f2937' } },
  };

  // One remark block: coloured badge + who/when on hover, then the text itself.
  const renderNote = (badge: string, badgeColor: string, by?: string, at?: string, text?: string) => (
    <div>
      <Tooltip title={`By ${by || ''}${at ? ' · ' + moment(at).format('DD MMM, hh:mm A') : ''}`} placement="top-start" arrow componentsProps={noteTooltipProps}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2, overflow: 'hidden' }}>
          <span style={{ fontSize: '7pt', fontWeight: 600, color: '#fff', backgroundColor: badgeColor, borderRadius: 3, padding: '1px 5px', flexShrink: 0 }}>{badge}</span>
          <span style={{ fontSize: '7pt', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            {by ? `By ${by}` : ''}{at ? ` · ${moment(at).format('DD MMM, hh:mm A')}` : ''}
          </span>
        </div>
      </Tooltip>
      {text && (
        <span style={{ fontSize: '8.5pt', color: '#374151', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {text}
        </span>
      )}
    </div>
  );

  const emptyCell = <Typography variant="caption" color="textSecondary">-</Typography>;

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "paymentDate",
      headerName: 'Date',
      type: "string",
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => (
        row.paymentDate ? (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>
            <span style={{ fontWeight: 500, fontSize: '9pt', color: '#111827' }}>{moment(row.paymentDate).format("DD MMM YYYY")}</span>
            <span style={{ color: '#6b7280', fontStyle: 'italic', fontSize: '8pt', marginTop: 2 }}>{moment(row.paymentDate).format("hh:mm A")}</span>
          </div>
        ) : emptyCell
      )
    },
    {
      field: "paymentTo",
      headerName: 'Party & Reference',
      type: "string",
      flex: 1.6,
      sortable: false,
      renderCell: ({ row }: GridRenderCellParams) => {
        const isAcct = row.paymentFor === 'account';
        const name = isAcct
          ? (row.locationId?.name || 'Self Account')
          : row.employee?.name || row.paymentTo?.legalName || '-';
        const refId = row.referenceId || (row.txnId ? `EXP-${String(row.txnId).padStart(3, '0')}` : '');
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
            <span style={{ fontWeight: 500 }}>{name}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
              {isAcct ? (
                <Chip label="Location" size="small" style={{ fontSize: 11, height: '16px', backgroundColor: '#e0f2f1', color: '#00695c', width: 'fit-content' }} />
              ) : row.paymentFor === 'employee' ? (
                <Chip label="Employee" size="small" style={{ fontSize: 11, height: '16px', backgroundColor: '#e3f2fd', color: '#1565c0', width: 'fit-content' }} />
              ) : (
                <Chip label="Supplier" size="small" style={{ fontSize: 11, height: '16px', backgroundColor: '#edf6ef', color: '#49a563', width: 'fit-content' }} />
              )}
              {refId && (
                <Chip label={refId} size="small" style={{ fontSize: 11, height: '16px', backgroundColor: '#f5f3ff', color: '#6366f1', fontWeight: 400, width: 'fit-content' }} />
              )}
            </div>
          </div>
        );
      }
    },
    {
      field: "expenseCategory",
      headerName: 'Category',
      type: "string",
      flex: 1,
      sortable: false,
      renderCell: ({ row }: GridRenderCellParams) => (
        <span style={{ whiteSpace: 'normal', lineHeight: 1.3 }}>{row?.expenseCategory?.name || ''}</span>
      )
    },
    {
      field: "amount",
      headerName: 'Payment',
      type: "string",
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: '1.3', width: '100%' }}>
          <span style={{ fontWeight: 600 }}>{row.amount > 0 ? Utils.formatNumber(parseFloat(row.amount).toFixed(2)) : ''}</span>
          <span style={{ fontStyle: 'italic', fontSize: '8pt', color: '#6b7280' }}>{row.paymentMode ? `via ${Utils.capitalizeFirstLetter(row.paymentMode.toLowerCase())}` : ''}</span>
        </div>
      )
    },
    {
      field: "remarks",
      headerName: 'Booked Remark',
      type: "string",
      flex: 1.6,
      sortable: false,
      renderCell: ({ row }: GridRenderCellParams) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
          {row?.remarks
            ? renderNote('Booked', '#e65100', row?.createdBy?.name, row?.createdAt, row.remarks)
            : emptyCell}
        </div>
      )
    },
    {
      field: "settlementRemarks",
      headerName: 'Paid Remark',
      type: "string",
      flex: 1.6,
      sortable: false,
      renderCell: ({ row }: GridRenderCellParams) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
          {row?.settlementRemarks
            ? renderNote('Paid', '#2e7d32', row?.updatedBy?.name, row?.updatedAt, row.settlementRemarks)
            : emptyCell}
        </div>
      )
    },
    {
      field: "paymentStatus",
      headerName: 'Status',
      type: "string",
      flex: 0.8,
      renderCell: ({ row }: GridRenderCellParams) => {
        const s = getStatusChipStyle(row.paymentStatus);
        return <Chip label={s.label} size="small" style={{ fontSize: 13, height: '24px', fontWeight: 600, backgroundColor: s.bg, color: s.color, borderRadius: '4px' }} />;
      }
    },
    {
      field: "action",
      headerName: 'Action',
      type: "string",
      flex: 1,
      sortable: false,
      renderCell: ({ row }: GridRenderCellParams) => (
        <Button
          variant="contained"
          size="small"
          disableElevation
          style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', borderRadius: 10 }}
          onClick={(e) => handleOpenActionMenu(e, row as ExpensePayment)}
          endIcon={<KeyboardArrowDownIcon />}
        >
          Actions
        </Button>
      )
    }
  ], [selectedAccount, categories, isAdmin, canApprove]);

  const isPaymentsLoading = !filters || paymentsStatus === ApiStatus.LOADING;

  return (
    <>
      <Typography className={'cardHeader'} variant="h4" component="div">
        <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "flex-start", gap: 1, paddingTop: '8px', width: '100%' }}>
          <div style={{ display: "flex", flexDirection: 'row', justifyContent: 'flex-start', alignItems: "flex-start", textAlign: "start", minWidth: 0, flex: 1 }}>
            <ExpenseFilter
              onFiltersChange={handleFiltersChange}
              onReset={() => { if (pinId) { searchParams.delete('pin'); setSearchParams(searchParams, { replace: true }); } }}
              currentFilters={filters || {}}
              categories={categories || []}
              hideVendorSearch={!!isEmployeeUser}
            />
          </div>
          <Box display="flex" gap={1} sx={{ flexShrink: 0 }}>
            {/* <Tooltip title={canExportExpense ? '' : 'You do not have permission to export expenses'}>
              <span>
                <Button
                  startIcon={canExportExpense ? <DownloadIcon /> : <LockIcon />}
                  onClick={() => handleExport('excel')}
                  disabled={!canExportExpense}
                  variant="outlined"
                  size="small"
                  sx={{ color: '#059669', borderColor: '#059669', height: 35, borderRadius: '7px', '&:hover': { borderColor: '#047857', backgroundColor: '#ecfdf5' }, textTransform: 'none' }}
                >
                  Export
                </Button>
              </span>
            </Tooltip>
            <Button
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              variant="outlined"
              size="small"
              sx={{ color: '#059669', borderColor: '#059669', height: 35, borderRadius: '7px', '&:hover': { borderColor: '#047857', backgroundColor: '#ecfdf5' }, textTransform: 'none' }}
            >
              Refresh
            </Button> */}
            <Tooltip title={canCreateExpense ? '' : 'You do not have permission to record expenses'}>
              <span>
                <Button
                  startIcon={canCreateExpense ? <AddIcon /> : <LockIcon />}
                  onClick={handleOpenExpense}
                  disabled={!canCreateExpense}
                  variant="contained"
                  size="small"
                  sx={{ backgroundColor: '#059669', height: 35, borderRadius: '7px', '&:hover': { backgroundColor: '#047857' }, textTransform: 'none' }}
                >
                  Record Expense
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Typography>

      <div style={{ height: "100vh", width: "100%", marginTop: '10px', overflow: 'auto', paddingRight: 10 }}>
        {isPaymentsLoading && rowCount === 0 ? (
          <Box sx={{ height: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress /></Box>
        ) : rowCount > 0 || pinnedExpense ? (
          <CustomDataGrid
            rowCount={rowCount}
            rowsPerPageOptions={defaultRowsPerPageOptions}
            page={page}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            onPageChange={handlePageChange}
            columns={columns}
            rows={shownPayments}
            getRowId={(row: any) => row._id}
            loading={isPaymentsLoading}
            rowHeight={76}
          />
            ) : (
              <Paper
                elevation={0}
                sx={{
                  height: '70vh',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#fafafb',
                  border: '2px dashed #e0e0e0',
                  borderRadius: 2,
                  p: 4
                }}
              >
                <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      backgroundColor: 'primary.light',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      mb: 3
                    }}
                  >
                    <ReceiptIcon sx={{ fontSize: 60, color: 'primary.main' }} />
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, color: '#333', mb: 1 }}
                  >
                    No Expenses Yet
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: '#666', mb: 4, lineHeight: 1.6 }}
                  >
                    Start tracking your expenses by recording your first expense. You can manage categories, track payments, and more.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={canCreateExpense ? <AddIcon /> : <LockIcon />}
                    onClick={handleOpenExpense}
                    disabled={!canCreateExpense}
                    title={canCreateExpense ? undefined : 'You do not have permission to record expenses'}
                    sx={{
                      px: 4,
                      py: 2,
                      borderRadius: 3,
                      textTransform: 'none',
                      fontSize: '16px',
                      fontWeight: 600,
                      letterSpacing: '0.5px',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-4px) scale(1.02)',
                        boxShadow: (theme: any) => `0 8px 25px -8px ${theme.palette.primary.main}`,
                      },
                      '&:active': {
                        transform: 'translateY(-2px) scale(0.98)',
                      },
                    }}
                  >
                    Record Expense
                  </Button>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 3, color: '#999' }}
                  >
                    Need help? Check our guide on expense management
                  </Typography>
                </Box>
              </Paper>
        )}
      </div>

      {/* Expense Form Drawer */}
      <Drawer
        disableEscapeKeyDown={false}
        anchor="right"
        open={openExpenseDrawer}
      >
        <DialogAddExpenseForm
          title={editExpense ? "Edit Expense" : "Record Expense Payment"}
          open={openExpenseDrawer}
          onClose={handleCloseExpense}
          editExpense={editExpense}
          isEmployeeUser={!!isEmployeeUser}
          loggedInEmployeeId={loggedInEmployeeId}
          loggedInEmployeeName={user?.name}
        />
      </Drawer>

      {/* Action Menu - Same as Sales Listing */}
      <StyledMenu
        id="expense-action-menu"
        MenuListProps={{ 'aria-labelledby': 'expense-action-button' }}
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
      >
        {isAdmin && actionMenuExpense && actionMenuExpense.paymentStatus === 'BOOKED' && (
          <MenuItem onClick={() => handleApproveExpense(actionMenuExpense)} disableRipple>
            <ApproveIcon style={{ color: '#16a34a', border: '1.5px solid #16a34a', borderRadius: '50%', backgroundColor: '#fff', padding: 1 }} />
            Approve
          </MenuItem>
        )}
        {isAdmin && actionMenuExpense && actionMenuExpense.paymentStatus === 'BOOKED' && (
          <MenuItem onClick={() => handleRejectExpense(actionMenuExpense)} disableRipple>
            <RejectIcon style={{ color: '#dc2626', border: '1.5px solid #dc2626', borderRadius: '50%', backgroundColor: '#fff', padding: 1 }} />
            Reject
          </MenuItem>
        )}
        {actionMenuExpense && actionMenuExpense.paymentStatus === 'REJECTED' && (
          <MenuItem onClick={() => handleEditExpense(actionMenuExpense)} disableRipple>
            <EditIcon color='primary' />
            Edit Expense
          </MenuItem>
        )}
        {isAdmin && actionMenuExpense && actionMenuExpense.paymentStatus === 'APPROVED' && (
          <MenuItem onClick={() => handleSettleExpense(actionMenuExpense)} disableRipple>
            <PaymentIcon color='success' />
            Pay Now
          </MenuItem>
        )}
        {actionMenuExpense && actionMenuExpense.attachments && actionMenuExpense.attachments.length > 0 && (
          <MenuItem onClick={() => { setAttachmentPreview({ open: true, attachments: actionMenuExpense.attachments as any[], currentIndex: 0 }); handleCloseActionMenu(); }} disableRipple>
            <DescriptionIcon style={{ color: '#7c8db0' }} />
            View Attachment{actionMenuExpense.attachments.length > 1 ? `s (${actionMenuExpense.attachments.length})` : ''}
          </MenuItem>
        )}
        {actionMenuExpense && (
          <MenuItem onClick={() => { handleOpenAudit(actionMenuExpense); handleCloseActionMenu(); }} disableRipple>
            <AssignmentIcon style={{ color: '#6b7280' }} />
            Audit Log
          </MenuItem>
        )}
        {actionMenuExpense && (
          <MenuItem onClick={() => handleDeleteFromMenu(actionMenuExpense)} disableRipple>
            <DeleteIcon style={{ color: '#e74c3c' }} />
            Delete
          </MenuItem>
        )}
      </StyledMenu>

      {/* Settle Expense Drawer */}
      <Drawer
        disableEscapeKeyDown={false}
        anchor="right"
        open={openSettleDialog}
      >
        <DialogSettleExpense
          open={openSettleDialog}
          expense={expenseToSettle}
          onClose={handleCloseSettleDialog}
        />
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setDialogExpense(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
          }
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: '#fef2f2', borderBottom: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: 2, py: 3, px: 3 }}>
          <Box sx={{ backgroundColor: '#fee2e2', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DeleteIcon sx={{ color: '#dc2626', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', fontSize: '1.1rem' }}>Delete Expense</Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>This action cannot be undone</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 3, px: 3 }}>
          <Box sx={{ mt:1,mb: 1, display: 'flex', gap: 3 }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#6b7280' }}>Expense ID</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#6366f1' }}>
                {dialogExpense?.referenceId || (dialogExpense?.txnId ? `EXP-${String(dialogExpense.txnId).padStart(3, '0')}` : '-')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#6b7280' }}>Against</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                {dialogExpense?.paymentFor === 'account'
                  ? ((dialogExpense?.locationId as any)?.name || 'Self Account')
                  : dialogExpense?.employee?.name || dialogExpense?.paymentTo?.legalName || '-'}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body1" sx={{ color: '#1f2937' }}>
            Are you sure you want to delete this expense of <b>{dialogExpense ? Utils.formatNumber(parseFloat(dialogExpense.amount as any) || 0, true, true) : ''}</b>?
          </Typography>
          {dialogExpense?.paymentStatus === 'PAID' && (
            <Typography variant="body2" sx={{ color: '#b45309', mt: 1.5, backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 1, px: 1.5, py: 1 }}>
              This is a paid expense. All associated journal entries and ledger postings will also be reversed.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb', px: 3, py: 2, gap: 1.5 }}>
          <Button onClick={() => { setDeleteDialogOpen(false); setDialogExpense(null); }} variant="outlined" sx={{ minWidth: 100, height: 40, textTransform: 'none', borderColor: '#d1d5db', color: '#374151' }}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" startIcon={<DeleteIcon sx={{ fontSize: 18 }} />} sx={{ minWidth: 140, height: 40, textTransform: 'none', fontWeight: 600, backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => { setApproveDialogOpen(false); setDialogExpense(null); setApproveReason(''); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
          }
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: '#f0fdf4', borderBottom: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: 2, py: 3, px: 3 }}>
          <Box sx={{ backgroundColor: '#dcfce7', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ApproveIcon sx={{ color: '#16a34a', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', fontSize: '1.1rem' }}>Approve Expense</Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>Approve this expense for payment</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 3, px: 3 }}>
          <Typography variant="body1" sx={{ color: '#1f2937', pt: 1, mb: 2 }}>
            Are you sure you want to approve this expense of <b>{dialogExpense ? Utils.formatNumber(parseFloat(dialogExpense.amount as any) || 0, true, true) : ''}</b>?
          </Typography>
          <TextField
            label="Remarks (optional)"
            fullWidth
            multiline
            rows={2}
            size="small"
            value={approveReason}
            onChange={(e) => setApproveReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb', px: 3, py: 2, gap: 1.5 }}>
          <Button onClick={() => { setApproveDialogOpen(false); setDialogExpense(null); setApproveReason(''); }} variant="outlined" sx={{ minWidth: 100, height: 40, textTransform: 'none', borderColor: '#d1d5db', color: '#374151' }}>Cancel</Button>
          <Button onClick={confirmApprove} variant="contained" startIcon={<ApproveIcon sx={{ fontSize: 18 }} />} sx={{ minWidth: 140, height: 40, textTransform: 'none', fontWeight: 600, backgroundColor: '#16a34a', '&:hover': { backgroundColor: '#15803d' } }}>Approve</Button>
        </DialogActions>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => { setRejectDialogOpen(false); setDialogExpense(null); setRejectReason(''); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
          }
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: '#fef2f2', borderBottom: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: 2, py: 3, px: 3 }}>
          <Box sx={{ backgroundColor: '#fee2e2', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RejectIcon sx={{ color: '#dc2626', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', fontSize: '1.1rem' }}>Reject Expense</Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>Provide a reason for rejection</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 3, px: 3 }}>
          <Typography variant="body1" sx={{ color: '#1f2937', pt: 1, mb: 2 }}>
            Are you sure you want to reject this expense of <b>{dialogExpense ? Utils.formatNumber(parseFloat(dialogExpense.amount as any) || 0, true, true) : ''}</b>?
          </Typography>
          <TextField
            label="Reason for rejection (optional)"
            fullWidth
            multiline
            rows={2}
            size="small"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb', px: 3, py: 2, gap: 1.5 }}>
          <Button onClick={() => { setRejectDialogOpen(false); setDialogExpense(null); setRejectReason(''); }} variant="outlined" sx={{ minWidth: 100, height: 40, textTransform: 'none', borderColor: '#d1d5db', color: '#374151' }}>Cancel</Button>
          <Button onClick={confirmReject} variant="contained" startIcon={<RejectIcon sx={{ fontSize: 18 }} />} sx={{ minWidth: 140, height: 40, textTransform: 'none', fontWeight: 600, backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}>Reject</Button>
        </DialogActions>
      </Dialog>

      {/* Audit Log Drawer (opens from the right) */}
      <Drawer
        anchor="right"
        open={auditDialogOpen}
        onClose={handleCloseAudit}
        PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, maxWidth: '100%' } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, px: 3, backgroundColor: '#f5f5f5', borderBottom: '1px solid #e5e7eb' }}>
            <AssignmentIcon sx={{ color: '#6b7280', fontSize: 26 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem' }}>Audit Logs</Typography>
            </Box>
            <IconButton size="small" onClick={handleCloseAudit}><CloseIcon /></IconButton>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', py: 2, px: 2 }}>
            {auditExpense && (
              <AuditTimeline entity="ExpensePayment" entityId={auditExpense._id} />
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Attachment Preview Dialog with Gallery */}
      <Dialog
        open={attachmentPreview.open}
        onClose={() => { setAttachmentPreview({ open: false, attachments: [], currentIndex: 0 }); setImgZoom(100); setImgPan({ x: 0, y: 0 }); }}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh', maxHeight: '90vh' } }}
        onKeyDown={(e) => {
          if (attachmentPreview.attachments.length <= 1) return;
          if (e.key === 'ArrowRight' && attachmentPreview.currentIndex < attachmentPreview.attachments.length - 1) {
            setAttachmentPreview(prev => ({ ...prev, currentIndex: prev.currentIndex + 1 }));
            setImgZoom(100); setImgPan({ x: 0, y: 0 });
          } else if (e.key === 'ArrowLeft' && attachmentPreview.currentIndex > 0) {
            setAttachmentPreview(prev => ({ ...prev, currentIndex: prev.currentIndex - 1 }));
            setImgZoom(100); setImgPan({ x: 0, y: 0 });
          }
        }}
      >
        {attachmentPreview.attachments.length > 0 && (() => {
          const att = attachmentPreview.attachments[attachmentPreview.currentIndex];
          const attName = att?.originalname || att?.attachmentLabel || 'Slip';
          const attUrl = att?.url || '';
          const isPdf = att?.mimeType === 'application/pdf' || attName.toLowerCase().endsWith('.pdf');
          return (
            <>
              <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {attName}
                  {attachmentPreview.attachments.length > 1 && (
                    <Typography component="span" variant="caption" sx={{ ml: 1, color: '#666' }}>
                      ({attachmentPreview.currentIndex + 1} / {attachmentPreview.attachments.length})
                    </Typography>
                  )}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {!isPdf && (
                    <>
                      <IconButton size="small" onClick={() => setImgZoom(z => Math.max(z - 25, 25))}><ZoomOutIcon sx={{ fontSize: 20 }} /></IconButton>
                      <Typography variant="caption" sx={{ minWidth: 35, textAlign: 'center', fontWeight: 600 }}>{imgZoom}%</Typography>
                      <IconButton size="small" onClick={() => setImgZoom(z => Math.min(z + 25, 300))}><ZoomInIcon sx={{ fontSize: 20 }} /></IconButton>
                      <IconButton size="small" onClick={() => { setImgZoom(100); setImgPan({ x: 0, y: 0 }); }}><ResetZoomIcon sx={{ fontSize: 18 }} /></IconButton>
                    </>
                  )}
                  <Button variant="outlined" size="small" startIcon={<DownloadIcon />} href={attUrl} target="_blank" download sx={{ textTransform: 'none', ml: 1 }}>Download</Button>
                  <IconButton size="small" onClick={() => { setAttachmentPreview({ open: false, attachments: [], currentIndex: 0 }); setImgZoom(100); }}><CloseIcon /></IconButton>
                </Box>
              </DialogTitle>
              <DialogContent sx={{ p: 2, textAlign: 'center', position: 'relative', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {isPdf ? (
                  <Box sx={{ position: 'relative', width: '100%', flex: 1 }}>
                    <Box sx={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={32} />
                      <Typography variant="caption" color="textSecondary">Loading PDF...</Typography>
                    </Box>
                    <iframe src={`https://docs.google.com/gview?url=${encodeURIComponent(attUrl)}&embedded=true`} width="100%" height="100%" title="PDF Preview" style={{ border: 'none', position: 'relative', zIndex: 1, backgroundColor: 'white' }} />
                  </Box>
                ) : (
                  <Box
                    sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: imgZoom > 100 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in', userSelect: 'none' }}
                    onWheel={(e) => {
                      e.preventDefault();
                      const delta = e.deltaY > 0 ? -10 : 10;
                      setImgZoom(z => {
                        const newZoom = Math.max(50, Math.min(z + delta, 400));
                        if (newZoom <= 100) { setImgPan({ x: 0, y: 0 }); }
                        return newZoom;
                      });
                    }}
                    onMouseDown={(e) => {
                      if (imgZoom > 100) {
                        setIsDragging(true);
                        setDragStart({ x: e.clientX - imgPan.x, y: e.clientY - imgPan.y });
                      }
                    }}
                    onMouseMove={(e) => {
                      if (isDragging && imgZoom > 100) {
                        setImgPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
                      }
                    }}
                    onMouseUp={() => setIsDragging(false)}
                    onMouseLeave={() => setIsDragging(false)}
                    onDoubleClick={() => { setImgZoom(100); setImgPan({ x: 0, y: 0 }); }}
                  >
                    <img
                      src={attUrl}
                      alt={attName}
                      draggable={false}
                      style={{
                        maxWidth: imgZoom === 100 ? '100%' : 'none',
                        maxHeight: imgZoom === 100 ? '100%' : 'none',
                        width: imgZoom !== 100 ? `${imgZoom}%` : undefined,
                        objectFit: 'contain',
                        transform: `translate(${imgPan.x}px, ${imgPan.y}px)`,
                        transition: isDragging ? 'none' : 'transform 0.1s ease',
                      }}
                    />
                  </Box>
                )}
                {/* Prev / Next buttons */}
                {attachmentPreview.attachments.length > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                    <Button size="small" variant="outlined" disabled={attachmentPreview.currentIndex === 0} onClick={() => { setAttachmentPreview(prev => ({ ...prev, currentIndex: prev.currentIndex - 1 })); setImgZoom(100); setImgPan({ x: 0, y: 0 }); }}>Prev</Button>
                    <Button size="small" variant="outlined" disabled={attachmentPreview.currentIndex === attachmentPreview.attachments.length - 1} onClick={() => { setAttachmentPreview(prev => ({ ...prev, currentIndex: prev.currentIndex + 1 })); setImgZoom(100); setImgPan({ x: 0, y: 0 }); }}>Next</Button>
                  </Box>
                )}
              </DialogContent>
            </>
          );
        })()}
      </Dialog>
    </>
  );
};

export default ExpenseList;
